import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import posthog from 'posthog-js';
import {
  Home, Briefcase, DollarSign, Landmark, Sparkles, Loader2, CheckCircle2, AlertTriangle, ArrowRight, Lock,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { home: Home, briefcase: Briefcase, 'dollar-sign': DollarSign, landmark: Landmark };

export default function Packs() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState({});
  const isPro = user?.subscription_tier === 'pro';

  const fetch = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/packs`, { withCredentials: true });
      setPacks(data);
    } catch (e) {
      toast.error('Failed to load packs');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleGenerate = async (packId, packTitle) => {
    if (!isPro) {
      toast.info('Packs are a Pro feature');
      navigate('/upgrade');
      return;
    }
    setGenerating((g) => ({ ...g, [packId]: true }));
    try {
      const { data } = await axios.post(`${API}/packs/${packId}/generate`, {}, { withCredentials: true });
      toast.success(`Generated ${data.total_generated} items in "${data.pack_title}"`);
      try { posthog.capture('pack_generated', { pack_id: packId, count: data.total_generated }); } catch (e) {}
      navigate('/applications');
    } catch (e) {
      if (e.response?.status === 403) { toast.error('Pro required'); navigate('/upgrade'); }
      else toast.error(e.response?.data?.detail || 'Failed to generate');
    } finally { setGenerating((g) => ({ ...g, [packId]: false })); }
  };

  if (loading) return <div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="container mx-auto px-4 md:px-8 py-8 max-w-5xl" data-testid="packs-page">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="w-6 h-6 text-primary" />
          <h1 className="font-heading text-3xl md:text-4xl">APPLICATION PACKS</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          One click. Every document, every form, every attachment — ready to go.
          {!isPro && <span className="ml-2 text-primary uppercase tracking-widest text-xs">Pro Feature</span>}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2" data-testid="packs-grid">
        {packs.map((p) => {
          const Icon = ICONS[p.icon] || Sparkles;
          const busy = generating[p.id];
          return (
            <div key={p.id} className="border border-border bg-background p-5 hover:border-primary transition-colors" data-testid={`pack-${p.id}`}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 border border-border flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-heading text-lg">{p.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{p.description}</div>
                  </div>
                </div>
                {!isPro && <Lock className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
              </div>

              <div className="mb-4">
                <div className="flex justify-between text-[10px] uppercase tracking-widest mb-1">
                  <span className="text-muted-foreground">READINESS</span>
                  <span className="font-mono">{p.readiness_pct}%</span>
                </div>
                <div className="h-1 bg-secondary">
                  <div className={`h-full transition-all ${p.readiness_pct === 100 ? 'bg-accent' : 'bg-primary'}`} style={{ width: `${p.readiness_pct}%` }} />
                </div>
              </div>

              {/* What it generates */}
              <div className="mb-4">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">GENERATES</div>
                <div className="flex flex-wrap gap-1.5">
                  {p.generates.map((g) => (
                    <span key={g} className="text-[10px] uppercase tracking-wider px-2 py-1 bg-secondary/40 border border-border font-mono">
                      {g.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>

              {/* Docs needed */}
              {p.docs_you_need.length > 0 && (
                <div className="mb-4 border-l-2 border-yellow-500 pl-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3 text-yellow-600" />
                    MISSING DOCS ({p.docs_you_need.length})
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Upload: {p.docs_you_need.map((d) => d.replace(/_/g, ' ')).join(', ')}
                  </div>
                </div>
              )}
              {p.docs_you_need.length === 0 && p.docs_you_have.length > 0 && (
                <div className="mb-4 flex items-center gap-2 text-xs text-accent">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  All required documents uploaded
                </div>
              )}

              <Button
                onClick={() => handleGenerate(p.id, p.title)}
                disabled={busy}
                className="w-full brutalist-shadow-sm uppercase text-xs tracking-wider"
                data-testid={`generate-${p.id}`}
              >
                {busy ? (<><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> GENERATING</>) : (
                  isPro ? (<>GENERATE PACK <ArrowRight className="w-3.5 h-3.5 ml-2" /></>) : (<><Lock className="w-3.5 h-3.5 mr-2" /> UPGRADE TO USE</>)
                )}
              </Button>
            </div>
          );
        })}
      </div>

      {!isPro && (
        <div className="mt-8 border border-border p-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-heading text-lg mb-1">UNLOCK ALL PACKS</div>
            <p className="text-xs text-muted-foreground">Pro members generate unlimited packs, get AI Coach reviews, and import forms from any URL.</p>
          </div>
          <Link to="/upgrade">
            <Button className="brutalist-shadow uppercase text-xs tracking-wider">
              UPGRADE — $9.99/MO
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}
